import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Instagram } from "lucide-react";
import content from "@/content";

const EASE = [0.22, 1, 0.36, 1];
const WEB3FORMS_URL = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = "7911601b-6435-44ae-8f9a-f4367c1890b7";

export default function EnquiryForm() {
  const e = content.enquire;
  const [form, setForm] = useState({ name: "", email: "", wedding_date: "", destination: "", message: "", botcheck: "" });
  const [services, setServices] = useState([]);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // "success" | "error" | null

  const toggleService = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so I can write back.");
      return;
    }
    setSending(true);
    setStatus(null);
    try {
      const res = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Enquiry – Vrisha Muni Website",
          name: form.name,
          email: form.email,
          wedding_date: form.wedding_date,
          destination: form.destination,
          services: services.join(", "),
          message: form.message,
          botcheck: form.botcheck, // honeypot - must stay empty
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Submission failed");
      setStatus("success");
      toast.success(e.successMessage);
      setForm({ name: "", email: "", wedding_date: "", destination: "", message: "", botcheck: "" });
      setServices([]);
    } catch {
      setStatus("error");
      toast.error(e.errorMessage);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-linen/25 focus:border-goldsoft outline-none py-3 text-sm text-linen placeholder:text-linen/35 transition-colors duration-300";

  return (
    <section id="enquire" data-testid="enquire-section" className="relative py-16 lg:py-24 bg-ink text-linen texture-grain">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="text-[11px] uppercase tracking-[0.3em] text-goldsoft mb-6"
          >
            {e.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-serif font-light text-4xl sm:text-5xl leading-tight"
            data-testid="enquire-heading"
          >
            {e.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="mt-6 text-sm text-linen/60 leading-relaxed max-w-md"
          >
            {e.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
            className="mt-12 space-y-5"
          >
            <a href={`mailto:${content.brand.email}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
              <span className="w-10 h-10 border border-linen/40 flex items-center justify-center group-hover:border-goldsoft group-hover:text-goldsoft transition-colors duration-300">
                <Mail size={15} />
              </span>
              <span className="text-sm group-hover:text-goldsoft transition-colors duration-300">{content.brand.email}</span>
            </a>
            <a href={content.brand.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="flex items-center gap-4 group">
              <span className="w-10 h-10 border border-linen/40 flex items-center justify-center group-hover:border-goldsoft group-hover:text-goldsoft transition-colors duration-300">
                <Instagram size={15} />
              </span>
              <span className="text-sm group-hover:text-goldsoft transition-colors duration-300">{content.brand.instagram}</span>
            </a>
          </motion.div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: EASE, delay: 0.2 }}
          onSubmit={submit}
          className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-linen/10"
          data-testid="enquiry-form"
        >
          {/* Honeypot - hidden from visitors, catches spam bots */}
          <input
            type="text"
            name="botcheck"
            data-testid="enquiry-honeypot"
            className="hidden"
            style={{ display: "none" }}
            tabIndex={-1}
            autoComplete="off"
            value={form.botcheck}
            onChange={(ev) => setForm({ ...form, botcheck: ev.target.value })}
          />
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
            <input
              data-testid="enquiry-name-input"
              className={inputCls}
              placeholder="Your name *"
              value={form.name}
              onChange={(ev) => setForm({ ...form, name: ev.target.value })}
            />
            <input
              data-testid="enquiry-email-input"
              className={inputCls}
              type="email"
              placeholder="Email address *"
              value={form.email}
              onChange={(ev) => setForm({ ...form, email: ev.target.value })}
            />
            <input
              data-testid="enquiry-date-input"
              className={inputCls}
              placeholder="Wedding date"
              value={form.wedding_date}
              onChange={(ev) => setForm({ ...form, wedding_date: ev.target.value })}
            />
            <input
              data-testid="enquiry-destination-input"
              className={inputCls}
              placeholder="Destination / City"
              value={form.destination}
              onChange={(ev) => setForm({ ...form, destination: ev.target.value })}
            />
          </div>

          <div className="mt-10">
            <div className="flex flex-wrap gap-3">
              {e.serviceOptions.map((s, i) => {
                const active = services.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`enquiry-service-chip-${i}`}
                    onClick={() => toggleService(s)}
                    className={`text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-all duration-300 ${
                      active
                        ? "bg-goldsoft text-ink border-goldsoft"
                        : "border-linen/25 text-linen/70 hover:border-goldsoft hover:text-goldsoft"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <textarea
            data-testid="enquiry-message-input"
            className="w-full mt-10 bg-transparent border border-linen/25 focus:border-goldsoft outline-none p-4 text-sm text-linen placeholder:text-linen/35 transition-colors duration-300 min-h-[120px] resize-none"
            placeholder="Tell me about your wedding - the city, the mood, anything you already know you want."
            value={form.message}
            onChange={(ev) => setForm({ ...form, message: ev.target.value })}
          />

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
            <button
              data-testid="enquiry-submit-btn"
              type="submit"
              disabled={sending}
              className="bg-goldsoft text-ink text-[11px] uppercase tracking-[0.25em] px-10 py-4 hover:bg-linen transition-colors duration-500 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send enquiry"}
            </button>
            {status === "success" && (
              <p data-testid="enquiry-success-message" className="text-sm text-goldsoft tracking-wide">
                {e.successMessage}
              </p>
            )}
            {status === "error" && (
              <p data-testid="enquiry-error-message" className="text-sm text-crimson tracking-wide">
                {e.errorMessage}
              </p>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
}
